import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Tailwind-aware class concatenation (shadcn standard helper). Combines
// clsx's conditional/array syntax with tailwind-merge's conflict resolution
// (e.g. `cn('p-4', 'p-2')` → 'p-2' instead of both).
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatRelativeTime(iso: string | null): string {
  if (!iso) return '/';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

export function shortId(uuid: string | null | undefined, len = 8): string {
  if (!uuid) return '/';
  return uuid.slice(0, len);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !isFinite(seconds) || seconds < 0) return '/';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${String(rm).padStart(2, '0')}m`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

// CF Images delivery URL. accountHash from Dashboard → Images → Variants/Keys.
// Variant 'public' is the legacy default; we use the flexible 'format=auto,quality=85'
// same as the iOS client for consistency.
export function cfImageUrl(imageId: string | null | undefined, accountHash: string, variant = 'format=auto,quality=85'): string | null {
  if (!imageId || !accountHash) return null;
  return `https://imagedelivery.net/${accountHash}/${imageId}/${variant}`;
}

export interface BroadcastStatus {
  kind: 'live' | 'loop' | 'upcoming' | 'ended';
  label: string;
  /** seconds until start (if upcoming) or remaining (if live). null for loop/ended. */
  seconds?: number;
}

export function computeBroadcastStatus(args: {
  startTimeIso: string;
  endTimeIso: string | null;
  isLoop: boolean;
}): BroadcastStatus {
  if (args.isLoop) return { kind: 'loop', label: 'LOOP' };
  const nowMs   = Date.now();
  const startMs = new Date(args.startTimeIso).getTime();
  const endMs   = args.endTimeIso ? new Date(args.endTimeIso).getTime() : null;
  if (nowMs < startMs) {
    return { kind: 'upcoming', label: 'UPCOMING', seconds: Math.round((startMs - nowMs) / 1000) };
  }
  if (endMs != null && nowMs > endMs) {
    return { kind: 'ended', label: 'ENDED' };
  }
  const remaining = endMs != null ? Math.round((endMs - nowMs) / 1000) : undefined;
  return { kind: 'live', label: 'LIVE', seconds: remaining };
}
